import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Bed, Bath, Square, Building2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (err) {
      logger.error("Failed to fetch property:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Building2 className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-medium text-slate-600 mb-4">Property Not Found</h2>
        <Link to="/properties">
          <Button variant="outline">Back to Properties</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <Link to="/properties" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1A1A2E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Properties</span>
        </Link>
      </div>

      {/* Hero Image */}
      <section className="relative">
        <div className="aspect-[21/9] max-h-[60vh] overflow-hidden">
          <img
            src={property.images[0] || "https://images.unsplash.com/photo-1595240015141-96aa95bb76b4?w=1200"}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="mb-6">
                <span className="px-3 py-1 bg-slate-100 text-xs font-medium uppercase tracking-wider text-[#1A1A2E]">
                  {property.market === "nyc" ? "New York City" : "Miami"}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight mb-4" data-testid="property-title">
                {property.title}
              </h1>

              <div className="flex items-center gap-2 text-slate-600 mb-8">
                <MapPin className="w-5 h-5" />
                <span>{property.address}</span>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 mb-12 p-6 bg-slate-50 border border-slate-100">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Bed className="w-5 h-5 text-[#0066FF]" />
                    <span className="text-2xl font-bold text-[#1A1A2E]">{property.bedrooms}</span>
                  </div>
                  <span className="text-sm text-slate-600">Bedrooms</span>
                </div>
                <div className="text-center border-x border-slate-200">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Bath className="w-5 h-5 text-[#0066FF]" />
                    <span className="text-2xl font-bold text-[#1A1A2E]">{property.bathrooms}</span>
                  </div>
                  <span className="text-sm text-slate-600">Bathrooms</span>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Square className="w-5 h-5 text-[#0066FF]" />
                    <span className="text-2xl font-bold text-[#1A1A2E]">{property.sqft.toLocaleString()}</span>
                  </div>
                  <span className="text-sm text-slate-600">Square Feet</span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-12">
                <h2 className="text-2xl font-light text-[#1A1A2E] mb-4">About This Property</h2>
                <p className="text-slate-600 leading-relaxed">{property.description}</p>
              </div>

              {/* Building */}
              {property.building_name && (
                <div className="mb-12">
                  <h2 className="text-2xl font-light text-[#1A1A2E] mb-4">Building</h2>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-[#0066FF]" />
                    <span className="text-lg text-slate-700">{property.building_name}</span>
                  </div>
                </div>
              )}

              {/* Amenities */}
              {property.amenities && property.amenities.length > 0 && (
                <div>
                  <h2 className="text-2xl font-light text-[#1A1A2E] mb-6">Amenities</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {property.amenities.map((amenity, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-slate-600">
                        <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="sticky top-24 bg-white border border-slate-100 p-8 shadow-float">
                <div className="mb-6">
                  <span className="text-sm text-slate-500">Investment Price</span>
                  <p className="text-4xl font-bold text-[#1A1A2E]">
                    {formatPrice(property.price)}
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Location</span>
                    <span className="font-medium text-[#1A1A2E]">{property.location}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Market</span>
                    <span className="font-medium text-[#1A1A2E]">{property.market === "nyc" ? "New York City" : "Miami"}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Status</span>
                    <span className="font-medium text-green-600 capitalize">{property.status}</span>
                  </div>
                </div>

                <Link to="/contact">
                  <Button className="btn-primary w-full py-6 text-base rounded-sm mb-4" data-testid="property-inquire-btn">
                    Inquire About This Property
                  </Button>
                </Link>

                <p className="text-xs text-slate-500 text-center">
                  Our team will contact you within 24 hours
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
